// TGCLOUD-1669 Phase 2 — the pinned person header.
//
// It sits outside ScrollableContent, so it does not scroll: once a clinician is
// deep in a person's clinical records this is the only thing tying the form to
// a patient, and misidentification is the clinical safety risk in this product.
//
// What goes in it, and in what order, is decided by buildPersonHeader. This
// file is presentation: how many facts fit, what happens to the rest, and how
// the two states that are not a normal named person are rendered.

import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { readableOn } from 'src/util/readableColour';

// AA-safe against white and against the faint tints the row can take.
const INK = '#1f2733';
const MUTED = '#55606d'; // 6.4:1 on white
const QUIET = '#5b6673'; // 5.8:1 on white
const LINE = '#dde3e9';

const CHIP_TONES = {
  neutral: { bg: '#eef2f5', fg: '#44515e' },
  teal: { bg: '#e8f6f9', fg: '#0e7186' },
  amber: { bg: '#fdf5e6', fg: '#8a5a08' }
};

function Chip({ tone = 'neutral', children, title }) {
  const c = CHIP_TONES[tone] || CHIP_TONES.neutral;
  return (
    <Box
      component="span"
      title={title}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 19,
        px: 0.75,
        borderRadius: '10px',
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: '.2px',
        whiteSpace: 'nowrap',
        bgcolor: c.bg,
        color: c.fg,
        flexShrink: 0
      }}
    >
      {children}
    </Box>
  );
}

function Fact({ fact }) {
  return (
    <Box
      component="span"
      title={`${fact.label}: ${fact.value}${
        fact.note ? ` (${fact.note})` : ''
      }`}
      sx={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: 0.5,
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden'
      }}
    >
      <Box
        component="span"
        sx={{
          fontSize: 9.5,
          fontWeight: 800,
          letterSpacing: '.4px',
          textTransform: 'uppercase',
          color: QUIET,
          flexShrink: 0
        }}
      >
        {fact.label}
      </Box>
      <Box
        component="span"
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: INK,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {fact.value}
      </Box>
      {fact.note && (
        // A word, not a colour or an icon: a partial date is a data-quality
        // fact a clinician has to be able to read aloud.
        <Box
          component="span"
          sx={{
            fontSize: 9.5,
            fontWeight: 700,
            color: '#8a5a08',
            flexShrink: 0
          }}
        >
          {fact.note}
        </Box>
      )}
    </Box>
  );
}

/**
 * @param {object} props
 * @param {object} props.header  the result of buildPersonHeader
 * @param {Function} props.onClose
 * @param {React.ReactNode} [props.lockStatus] rendered inline with the id row
 * @param {number} [props.maxFacts] how many facts the run shows before folding
 */
export default function PersonHeaderBand({
  header,
  onClose,
  lockStatus = null,
  maxFacts = 5,
  headerColour,
  // TGCLOUD-1669 Phase 3 — the persistent controls. They live in the header
  // rather than inside a section because that is the complaint: the record
  // browser was reachable only from a magnifier inside the CLINICAL RECORDS
  // accordion, and the change history only from small clocks inside each one.
  actions = null
}) {
  const [anchor, setAnchor] = useState(null);

  // The patient's NAME, in a colour a tenant admin picked for branding.
  // `theme.header` defaults to #2ba9bf, which is 2.79:1 on white, so on a
  // default deployment the one line identifying whose chart this is was the
  // least legible text in the band. Darkened only as far as AA needs, so a
  // tenant whose colour already passes keeps it exactly.
  //
  // 4.5 rather than the 3:1 large-text allowance on purpose: the identifier
  // variant sets this at 1.05rem, which is 16.8px and NOT large text, and one
  // floor for both states is one thing to get wrong instead of two.
  const nameColour = useMemo(() => readableOn(headerColour), [headerColour]);

  // Only facts that HAVE a value compete for the visible run. The rest are not
  // dropped: they move into the overflow marked "not recorded", so a clinician
  // can still confirm a field is genuinely empty rather than merely truncated.
  const { visible, folded, foldedWithValue } = useMemo(() => {
    const facts = header?.facts || [];
    const withValue = facts.filter((f) => f.value);
    const withoutValue = facts.filter((f) => !f.value);
    const rest = withValue.slice(maxFacts);
    return {
      visible: withValue.slice(0, maxFacts),
      folded: rest.concat(withoutValue),
      foldedWithValue: rest.length
    };
  }, [header, maxFacts]);

  if (!header) return null;

  const isFamily = header.kind === 'family';

  return (
    <Box
      sx={{
        flexShrink: 0,
        px: 1.5,
        pt: 1.25,
        pb: 1,
        borderBottom: `1px solid ${LINE}`,
        bgcolor: 'background.paper'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {header.overline && (
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                // NOT text.disabled. That is rgba(0, 0, 0, 0.38), which
                // composites to about 2.8:1 on white and fails AA at any
                // size. This overline carries the words "DECEASED" and
                // "PROBAND", so it is not decoration.
                color: MUTED,
                lineHeight: 1.2
              }}
            >
              {header.overline}
            </Typography>
          )}
          <Typography
            component="h2"
            title={header.primary}
            sx={{
              color: nameColour,
              fontWeight: 700,
              fontSize: header.isIdentifierPrimary ? '1.05rem' : '1.2rem',
              // With no name the id IS the identity, so it is set in the
              // monospace face to read as an identifier rather than as a word.
              fontFamily: header.isIdentifierPrimary
                ? 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'
                : undefined,
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {header.primary}
          </Typography>
        </Box>
        {/* Only where there is something to close. In the drawer this
            deselects the node and shuts the panel; a person TAB has no node to
            deselect and is closed by its own tab control, so the caller passes
            no handler and the control should not be there at all. A button
            that does nothing when pressed is worse than an absent one. */}
        {onClose && (
          <IconButton
            onClick={onClose}
            sx={{ p: 0.75, mt: -0.25 }}
            aria-label="Close the person panel"
          >
            <HighlightOffIcon />
          </IconButton>
        )}
      </Box>

      {!isFamily && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 0.5,
            mt: 0.25
          }}
        >
          {header.subtitle && (
            <Typography
              title={header.subtitle}
              sx={{
                fontSize: '0.72rem',
                color: MUTED,
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
                maxWidth: '100%'
              }}
            >
              {header.subtitle}
            </Typography>
          )}
          {header.proband && (
            <Chip tone="teal" title="The index case for this family">
              Proband
            </Chip>
          )}
          {header.deceased && <Chip>Deceased</Chip>}
          {header.secondaryRecord && (
            <Chip
              tone="amber"
              title="A linked secondary record cannot be edited"
            >
              Secondary
            </Chip>
          )}
          {lockStatus}
        </Box>
      )}

      {!isFamily && (visible.length > 0 || folded.length > 0) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            flexWrap: 'wrap',
            columnGap: 1.25,
            rowGap: 0.25,
            mt: 0.75,
            minWidth: 0
          }}
        >
          {visible.map((f) => (
            <Fact key={f.key} fact={f} />
          ))}
          {folded.length > 0 && (
            // A real control, not a tooltip, so it is keyboard reachable.
            <Box
              component="button"
              type="button"
              onClick={(e) => setAnchor(e.currentTarget)}
              aria-label={
                foldedWithValue
                  ? `Show ${foldedWithValue} more person details`
                  : `Show ${folded.length} person details that are not recorded`
              }
              sx={{
                border: `1px dashed ${LINE}`,
                bgcolor: 'transparent',
                borderRadius: '10px',
                height: 19,
                px: 0.75,
                fontSize: 10.5,
                fontWeight: 800,
                color: MUTED,
                cursor: 'pointer',
                flexShrink: 0,
                '&:hover': { borderColor: '#0e7186', color: '#0e7186' }
              }}
            >
              {/* QA: a person with almost nothing recorded showed
                  "SEX Male · +8 more", which promises eight further facts when
                  all eight are empty. The chip now says which it is. Both open
                  the same panel, because being able to confirm a field is
                  genuinely blank is worth a click. */}
              {foldedWithValue
                ? `+${foldedWithValue} more`
                : `${folded.length} not recorded`}
            </Box>
          )}
        </Box>
      )}

      {!isFamily && actions && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.75,
            mt: 1
          }}
        >
          {actions}
        </Box>
      )}

      {header.tags?.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
          {header.tags.map((t, i) => (
            <Chip key={`${t.tag}-${i}`}>{t.tag}</Chip>
          ))}
        </Box>
      )}

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 1.25, maxWidth: 300 } } }}
      >
        {folded.map((f) => (
          <Box
            key={f.key}
            sx={{ display: 'flex', gap: 1, alignItems: 'baseline', py: 0.25 }}
          >
            <Box
              component="span"
              sx={{
                fontSize: 9.5,
                fontWeight: 800,
                letterSpacing: '.4px',
                textTransform: 'uppercase',
                color: QUIET,
                width: 96,
                flexShrink: 0
              }}
            >
              {f.label}
            </Box>
            <Box
              component="span"
              sx={{
                fontSize: 12.5,
                color: f.value ? INK : QUIET,
                fontStyle: f.value ? 'normal' : 'italic',
                fontWeight: f.value ? 600 : 400,
                minWidth: 0,
                wordBreak: 'break-word'
              }}
            >
              {f.value || 'not recorded'}
              {f.note ? ` (${f.note})` : ''}
            </Box>
          </Box>
        ))}
      </Popover>
    </Box>
  );
}
